import {
  Dialog,
  DialogHeader,
  DialogContent,
  DialogHeading
} from 'aquilla/core/molecules/Dialog';
import { useRef } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import useCompete from '@stores/compete';
import Cross from 'aquilla/icons/misc/Cross';
import Divider from 'aquilla/core/atoms/Divider';
import useWindowSize from '@hooks/useWindowSize';
import Button from 'aquilla/core/molecules/Button';
import Typography from 'aquilla/core/atoms/Typography';
import AquillaDrawer from 'aquilla/core/organisms/Drawer';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import CompeteInfo from './CompeteInfo';
import RatingChart from './RatingChart/RatingChart';
import RatingPill from './RatingActivity/RatingPill';
import useRatingProgressInfo from './useRatingProgressInfo';

const StyledDialog = styled(Dialog)`
  height: 640px;
`;

const StyAquillaDrawer = styled(AquillaDrawer)`
  ${mediaQuery.uptoMobile} {
    width: 100%;
    margin-top: 0;
    & > .aquilla-drawer-header {
      padding: ${Spacings.SPACING_4B};
      width: ${Spacings.SPACING_10};
    }
    & > .aquilla-drawer-content {
      padding: 0;
    }
    max-height: 100vh;
    ::-webkit-scrollbar {
      display: none;
    }
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

const StyledDialogContent = styled(DialogContent)`
  padding-bottom: ${Spacings.SPACING_3B};
  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
  ${mediaQuery.uptoMobile} {
    padding: 0 ${Spacings.SPACING_4B};
    margin-bottom: 0;
  }
`;

const HeaderWrapper = styled.div`
  grid-column: span 24;
  height: ${Spacings.SPACING_27B};
  width: 100%;
  position: sticky;
  z-index: 1;
  padding: ${Spacings.SPACING_4B};
  top: 0;
  background: var(--color-base-1);
  border-bottom: ${Spacings.SPACING_1} solid var(--color-divider);
`;

const HeadingWrapper = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${Spacings.SPACING_4B};
`;

const Heading = styled(Typography)`
  margin-right: ${Spacings.SPACING_2B};
  margin-left: ${Spacings.SPACING_4B};
`;

const DurationFilters = styled.div`
  height: ${Spacings.SPACING_9B};
  display: flex;
  overflow: hidden;
  width: min-content;
  align-items: center;
`;

const StyledButton = styled(Button)`
  height: ${Spacings.SPACING_9B};
  border-radius: ${Spacings.SPACING_8B};
  border: 1px solid var(--color-i-divider);
  background-color: unset;
  margin-right: ${Spacings.SPACING_3B};
  width: max-content;
  font-weight: ${Fonts.SEMIBOLD};
  :focus {
    background-color: unset;
    border: 1px solid var(--color-base-fill);
  }
  ${({ $isSelected }) => {
    if ($isSelected) {
      return css`
        border: 1px solid var(--color-base-fill);
      `;
    }
  }}
`;

const ChartWrapper = styled.div`
  height: 224px;
  margin-top: ${Spacings.SPACING_6B};
  margin-bottom: ${Spacings.SPACING_8B};
  width: 100%;
  display: grid;
  grid-template-rows: 1fr 42px;
  grid-row-gap: ${Spacings.SPACING_6B};
  ${mediaQuery.uptoMobile} {
    height: 240px;
    margin-top: ${Spacings.SPACING_8B};
    margin-bottom: 0;
  }
`;

const RatingWrapper = styled.div`
  .rating-pill {
    margin: ${Spacings.SPACING_3B} 0;
  }
  .rating-pill:nth-of-type(1) {
    margin-top: ${Spacings.SPACING_6B};
  }
  .rating-pill:nth-of-type(4) {
    margin-bottom: 0;
  }
`;

const StyledDivider = styled(Divider)`
  margin: ${Spacings.SPACING_6B} 0;
`;

const RatingProgressModal = ({ isModalOpen, onCloseModal }) => {
  const dialogRef = useRef(null);
  const { isMobile } = useWindowSize();
  const { data } = useRatingProgressInfo();
  const { selectedDuration, setSelectedDuration } = useCompete();

  const renderDurationFilters = () => {
    return (
      <DurationFilters>
        {data.durationFilters?.items?.map((item) => {
          const isSelected = selectedDuration === item.value;
          const handleClick = () => setSelectedDuration(item.value);
          return (
            <StyledButton
              key={item.title}
              label={item.title}
              variant="secondary"
              $isSelected={isSelected}
              onClick={handleClick}
            />
          );
        })}
      </DurationFilters>
    );
  };

  const renderChartAndInfo = () => {
    return (
      <ChartWrapper>
        {isModalOpen && <RatingChart />}
        <CompeteInfo isInsideModal />
      </ChartWrapper>
    );
  };

  const renderRatingsList = () => {
    return (
      <RatingWrapper>
        <Typography variant="h5">Rating activity</Typography>
        {data.userActivity?.map((rankInfo) => {
          return (
            <RatingPill key={rankInfo.opponentUserUid} rankInfo={rankInfo} />
          );
        })}
      </RatingWrapper>
    );
  };

  if (isMobile) {
    return (
      <StyAquillaDrawer
        anchor="right"
        open={isModalOpen}
        onClose={onCloseModal}
        showCloseIcon={!isMobile}
      >
        <HeaderWrapper>
          <HeadingWrapper>
            <Cross onClick={onCloseModal} height={24} width={24} />
            <Heading variant="h5">Rating progress</Heading>
          </HeadingWrapper>
          {renderDurationFilters()}
        </HeaderWrapper>
        <StyledDialogContent>
          {renderChartAndInfo()}
          <StyledDivider orientation="horizontal" />
          {renderRatingsList()}
        </StyledDialogContent>
      </StyAquillaDrawer>
    );
  }

  return (
    <>
      <StyledDialog size="small" open={isModalOpen} close={onCloseModal}>
        <DialogHeader>
          <Cross height={24} width={24} onClick={onCloseModal} />
          <DialogHeading>Rating progress</DialogHeading>
        </DialogHeader>
        <StyledDialogContent ref={dialogRef}>
          {renderDurationFilters()}
          {renderChartAndInfo()}
          {renderRatingsList()}
        </StyledDialogContent>
      </StyledDialog>
    </>
  );
};

export default RatingProgressModal;
